import { OrderHistory, EmailProductData } from '../../stores/appOrderHistoryStore';
import { Platform } from '../../types/Platforms';
import { getProductQuantity, calculateProductTotal } from '../platformUtils';
import { getCategoryDisplayName } from '../../components/orderhistory/categoryUtils';
import { productKey, inRange, ReportRange } from './shared';

/**
 * Pure, React-free computation for the Price Inflation Impact report (R5):
 * R4's price math across ALL suppliers — where is a cost increase coming from,
 * price or volume?
 *
 * For every product bought in BOTH the selected period and the previous
 * equal-length period we split its spend delta into an exact, residual-free
 * price/volume decomposition (Paasche-style price, Laspeyres-style volume):
 *   priceImpact  = (avgPriceNow − avgPricePrev) × qtyNow
 *   volumeImpact = (qtyNow − qtyPrev) × avgPricePrev
 * These two sum EXACTLY to (spendNow − spendPrev) for that product. Products
 * bought in only one period land in the "new" / "no longer bought" buckets, so
 * the grand total reconciles: ΔTotalSpend = Σprice + Σvolume + new − dropped.
 *
 * Unit-consistency: when a product's package size (stdAmount × stdMultiplier)
 * changes between the two periods the order-unit comparison is misleading, so we
 * compare €/base-unit instead and flag the row. When quantityInfo is missing on
 * either side we fall back to €/order-unit and still flag it.
 *
 * Inputs are already scoped to the selected restaurant by the data-loading hooks;
 * period filtering happens here. All € on the ex-VAT basis (`calculateProductTotal`
 * exVat=true), quantities via `getProductQuantity` (handles per-kg platforms).
 * Supplier identity mirrors `getSupplierKey()` (`orderHistoryCalculations.ts:52-58`).
 */

/** Basis used for a product's price/volume split. */
export type InflationBasis = 'order' | 'base';

/** Reason a row carries the unit-consistency flag. */
export type InflationFlagReason = 'packageSizeChanged' | 'missingQuantityInfo';

/** One product bought in BOTH periods, with its price/volume decomposition. */
export interface InflationProductRow {
  key: string;
  name: string;
  articleId: string;
  category?: string;
  categoryDisplay: string | null;
  supplierKey: string;
  supplierLabel: string;
  platform: Platform;
  unknownSupplierName?: string;
  /** Ex-VAT spend this period. */
  spendNow: number;
  /** Ex-VAT spend in the previous period. */
  spendPrev: number;
  /** Quantity this period, in the chosen basis (order units or base units). */
  qtyNow: number;
  /** Quantity in the previous period, same basis. */
  qtyPrev: number;
  /** spendNow / qtyNow. */
  avgPriceNow: number;
  /** spendPrev / qtyPrev. */
  avgPricePrev: number;
  /** € attributable to the unit-price change. */
  priceImpactEur: number;
  /** € attributable to the quantity change. */
  volumeImpactEur: number;
  /** spendNow − spendPrev (= priceImpactEur + volumeImpactEur). */
  spendDeltaEur: number;
  /** % change avgPricePrev → avgPriceNow. */
  priceChangePercent: number;
  /** Which unit basis the split used. */
  basis: InflationBasis;
  /** True when the row could not be compared on consistent order units. */
  unitFlag: boolean;
  unitFlagReason?: InflationFlagReason;
  /** True when a period's net quantity is ≤ 0 (e.g. credit notes): the spend delta
   * cannot be split into price/volume, so it is counted entirely as volume. */
  notDecomposable: boolean;
}

export interface InflationOption {
  value: string;
  label: string;
}

export interface InflationSupplierOption extends InflationOption {
  platform: Platform;
  unknownSupplierName?: string;
}

export interface InflationReportData {
  /** True in the "all" view (no previous period exists). */
  comparisonHidden: boolean;
  /** True when there are orders in BOTH the current and previous period. */
  hasData: boolean;
  totalSpendNow: number;
  totalSpendPrev: number;
  /** totalSpendNow − totalSpendPrev. */
  totalSpendChange: number;
  /** Σ price impacts over both-period products. */
  priceImpactTotal: number;
  /** Σ volume impacts over both-period products. */
  volumeImpactTotal: number;
  /** Σ spend of products bought only this period. */
  newProductsSpend: number;
  /** Σ spend of products bought only in the previous period (a positive number). */
  droppedProductsSpend: number;
  newProductsCount: number;
  droppedProductsCount: number;
  /** All both-period products, sorted by absolute spend delta desc. */
  products: InflationProductRow[];
  /** Top 10 risers by price impact (priceImpactEur > 0), desc. */
  topRisers: InflationProductRow[];
  /** Top 10 fallers by price impact (priceImpactEur < 0), most negative first. */
  topFallers: InflationProductRow[];
  /** Count of both-period rows carrying the unit-consistency flag. */
  flaggedCount: number;
  supplierOptions: InflationSupplierOption[];
  categoryOptions: InflationOption[];
}

// --- supplier identity (mirrors getSupplierKey, orderHistoryCalculations.ts:52-58) ---

function supplierKeyOf(order: OrderHistory): string {
  const platform = (order.platform || '').toLowerCase();
  if (platform === 'any_invoice' && order.unknownSupplierName) {
    return order.unknownSupplierName;
  }
  return platform;
}

/** Display label for a supplier: the supplier name for any_invoice, else the platform.
 * '' when nothing is available — the component supplies a translated fallback. */
function supplierLabelOf(order: OrderHistory): string {
  const platform = (order.platform || '').trim();
  if (platform.toLowerCase() === 'any_invoice' && order.unknownSupplierName) {
    return order.unknownSupplierName.trim();
  }
  return platform || (order.unknownSupplierName || '').trim();
}

// --- per-period aggregation ----------------------------------------------

interface PeriodAgg {
  key: string;
  name: string;
  articleId: string;
  category?: string;
  mainCategory?: string;
  supplierKey: string;
  supplierLabel: string;
  platform: Platform;
  unknownSupplierName?: string;
  /** Ex-VAT Σ price×qty. */
  spend: number;
  /** Σ getProductQuantity (order units). */
  orderQty: number;
  /** Σ qty × stdAmount × stdMultiplier over lines that have quantityInfo. */
  baseUnits: number;
  /** stdAmount × stdMultiplier of the latest line that had quantityInfo, else null. */
  latestStdSize: number | null;
  latestDate: number;
}

interface AggregateResult {
  byProduct: Map<string, PeriodAgg>;
  totalSpend: number;
}

function aggregatePeriod(
  orders: OrderHistory[],
  range: ReportRange,
  supplierFilter: string,
  categoryFilter: string
): AggregateResult {
  const byProduct = new Map<string, PeriodAgg>();
  let totalSpend = 0;

  orders.forEach((order) => {
    if (!inRange(new Date(order.orderDate), range)) return;
    const sKey = supplierKeyOf(order);
    if (supplierFilter !== 'all' && sKey !== supplierFilter) return;
    const orderDate = new Date(order.orderDate).getTime();

    order.productData.forEach((product: EmailProductData) => {
      if (categoryFilter !== 'all' && (product.category || '') !== categoryFilter) return;

      const pKey = productKey(order.platform, product.articleId, product.name);
      const key = `${sKey}||${pKey}`;
      const qty = getProductQuantity(product, order.platform);
      const spend = calculateProductTotal(product, order.platform, true);
      const stdAmount = product.quantityInfo?.stdAmount ?? 0;
      const stdMult = product.quantityInfo?.stdMultiplier ?? 0;
      const hasQty = !!(stdAmount && stdMult);

      const agg =
        byProduct.get(key) ||
        ({
          key,
          name: product.name,
          articleId: product.articleId,
          category: product.category,
          mainCategory: product.mainCategory,
          supplierKey: sKey,
          supplierLabel: supplierLabelOf(order),
          platform: order.platform,
          unknownSupplierName: order.unknownSupplierName,
          spend: 0,
          orderQty: 0,
          baseUnits: 0,
          latestStdSize: null,
          latestDate: -Infinity,
        } as PeriodAgg);

      agg.spend += spend;
      agg.orderQty += qty;
      if (hasQty) agg.baseUnits += qty * stdAmount * stdMult;
      // Representative package size = the latest order line that carried quantityInfo.
      if (hasQty && orderDate >= agg.latestDate) {
        agg.latestStdSize = stdAmount * stdMult;
        agg.latestDate = orderDate;
      }

      totalSpend += spend;
      byProduct.set(key, agg);
    });
  });

  return { byProduct, totalSpend };
}

// --- main entry point -----------------------------------------------------

export interface InflationReportInputs {
  orders: OrderHistory[];
  range: ReportRange;
  previous: ReportRange | null;
  /** Selected supplier filter (supplierKey) or 'all'. */
  supplierFilter: string;
  /** Selected category filter (product.category) or 'all'. */
  categoryFilter: string;
}

function emptyData(comparisonHidden: boolean): InflationReportData {
  return {
    comparisonHidden,
    hasData: false,
    totalSpendNow: 0,
    totalSpendPrev: 0,
    totalSpendChange: 0,
    priceImpactTotal: 0,
    volumeImpactTotal: 0,
    newProductsSpend: 0,
    droppedProductsSpend: 0,
    newProductsCount: 0,
    droppedProductsCount: 0,
    products: [],
    topRisers: [],
    topFallers: [],
    flaggedCount: 0,
    supplierOptions: [],
    categoryOptions: [],
  };
}

/** Build the supplier + category filter option lists from BOTH periods' orders,
 * unfiltered, so the picker always offers everything regardless of the current
 * selection. */
function buildOptions(
  orders: OrderHistory[],
  range: ReportRange,
  previous: ReportRange
): { supplierOptions: InflationSupplierOption[]; categoryOptions: InflationOption[] } {
  const suppliers = new Map<string, InflationSupplierOption>();
  const categories = new Map<string, string>();

  orders.forEach((order) => {
    const date = new Date(order.orderDate);
    if (!inRange(date, range) && !inRange(date, previous)) return;
    const sKey = supplierKeyOf(order);
    if (!suppliers.has(sKey)) {
      suppliers.set(sKey, {
        value: sKey,
        label: supplierLabelOf(order),
        platform: order.platform,
        unknownSupplierName: order.unknownSupplierName,
      });
    }
    order.productData.forEach((product) => {
      if (product.category && !categories.has(product.category)) {
        categories.set(
          product.category,
          getCategoryDisplayName(product.category, product.mainCategory) || product.category
        );
      }
    });
  });

  const supplierOptions = Array.from(suppliers.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  const categoryOptions = Array.from(categories.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return { supplierOptions, categoryOptions };
}

export function computeInflationReport(inputs: InflationReportInputs): InflationReportData {
  const { orders, range, previous, supplierFilter, categoryFilter } = inputs;

  // No previous period ("all" view) → decomposition impossible.
  if (!previous) return emptyData(range.comparisonHidden);

  const { byProduct: nowMap, totalSpend: totalSpendNow } = aggregatePeriod(
    orders,
    range,
    supplierFilter,
    categoryFilter
  );
  const { byProduct: prevMap, totalSpend: totalSpendPrev } = aggregatePeriod(
    orders,
    previous,
    supplierFilter,
    categoryFilter
  );

  const { supplierOptions, categoryOptions } = buildOptions(orders, range, previous);

  if (nowMap.size === 0 && prevMap.size === 0) {
    return { ...emptyData(false), supplierOptions, categoryOptions };
  }

  const products: InflationProductRow[] = [];
  let priceImpactTotal = 0;
  let volumeImpactTotal = 0;
  let newProductsSpend = 0;
  let droppedProductsSpend = 0;
  let newProductsCount = 0;
  let droppedProductsCount = 0;
  let flaggedCount = 0;

  // Both-period products → price/volume split.
  Array.from(nowMap.entries()).forEach(([key, now]) => {
    const prev = prevMap.get(key);
    if (!prev) {
      newProductsSpend += now.spend;
      newProductsCount += 1;
      return;
    }

    const sizeNow = now.latestStdSize;
    const sizePrev = prev.latestStdSize;

    let basis: InflationBasis;
    let unitFlag = false;
    let unitFlagReason: InflationFlagReason | undefined;

    if (sizeNow == null || sizePrev == null) {
      basis = 'order';
      unitFlag = true;
      unitFlagReason = 'missingQuantityInfo';
    } else if (sizeNow !== sizePrev) {
      basis = 'base';
      unitFlag = true;
      unitFlagReason = 'packageSizeChanged';
    } else {
      basis = 'order';
    }

    let qtyNow = basis === 'base' ? now.baseUnits : now.orderQty;
    let qtyPrev = basis === 'base' ? prev.baseUnits : prev.orderQty;
    // Guard: base units somehow zero → fall back to order units to keep the split exact.
    if (basis === 'base' && (qtyNow <= 0 || qtyPrev <= 0)) {
      basis = 'order';
      qtyNow = now.orderQty;
      qtyPrev = prev.orderQty;
    }

    // A period with net qty ≤ 0 (credit notes) has no meaningful average price, so
    // the delta cannot be decomposed — count the full spend delta as volume to keep
    // the totals banner reconciling exactly, and flag the row.
    const notDecomposable = qtyNow <= 0 || qtyPrev <= 0;
    const avgPriceNow = qtyNow > 0 ? now.spend / qtyNow : 0;
    const avgPricePrev = qtyPrev > 0 ? prev.spend / qtyPrev : 0;
    const priceImpactEur = notDecomposable ? 0 : (avgPriceNow - avgPricePrev) * qtyNow;
    const volumeImpactEur = notDecomposable
      ? now.spend - prev.spend
      : (qtyNow - qtyPrev) * avgPricePrev;
    const spendDeltaEur = priceImpactEur + volumeImpactEur;

    priceImpactTotal += priceImpactEur;
    volumeImpactTotal += volumeImpactEur;
    if (unitFlag) flaggedCount += 1;

    products.push({
      key,
      name: now.name,
      articleId: now.articleId,
      category: now.category,
      categoryDisplay: getCategoryDisplayName(now.category, now.mainCategory),
      supplierKey: now.supplierKey,
      supplierLabel: now.supplierLabel,
      platform: now.platform,
      unknownSupplierName: now.unknownSupplierName,
      spendNow: now.spend,
      spendPrev: prev.spend,
      qtyNow,
      qtyPrev,
      avgPriceNow,
      avgPricePrev,
      priceImpactEur,
      volumeImpactEur,
      spendDeltaEur,
      priceChangePercent:
        !notDecomposable && avgPricePrev !== 0
          ? ((avgPriceNow - avgPricePrev) / avgPricePrev) * 100
          : 0,
      basis,
      unitFlag,
      unitFlagReason,
      notDecomposable,
    });
  });

  // Dropped products (only in the previous period).
  Array.from(prevMap.entries()).forEach(([key, prev]) => {
    if (!nowMap.has(key)) {
      droppedProductsSpend += prev.spend;
      droppedProductsCount += 1;
    }
  });

  products.sort((a, b) => Math.abs(b.spendDeltaEur) - Math.abs(a.spendDeltaEur));

  const topRisers = products
    .filter((p) => p.priceImpactEur > 0)
    .sort((a, b) => b.priceImpactEur - a.priceImpactEur)
    .slice(0, 10);
  const topFallers = products
    .filter((p) => p.priceImpactEur < 0)
    .sort((a, b) => a.priceImpactEur - b.priceImpactEur)
    .slice(0, 10);

  return {
    comparisonHidden: false,
    hasData: true,
    totalSpendNow,
    totalSpendPrev,
    totalSpendChange: totalSpendNow - totalSpendPrev,
    priceImpactTotal,
    volumeImpactTotal,
    newProductsSpend,
    droppedProductsSpend,
    newProductsCount,
    droppedProductsCount,
    products,
    topRisers,
    topFallers,
    flaggedCount,
    supplierOptions,
    categoryOptions,
  };
}
